import PendingAssetEditor from "../../shared/PendingAssetEditor";
import React, { useEffect, useState } from "react";
import axios from "axios";
import PendingAssetDeleteButton from "../../shared/PendingAssetDeleteButton";
import "./pendingAllAsset.css";
import { Link, useNavigate } from "react-router-dom";
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import AssetPagination from "../../shared/AssetPagination";

const PendingAllAsset = () => {
  const [assetRegisterDetails, setAssetRegisterDetails] = useState([]);
  const [transferAssetDetails, setTransferAssetDetails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assetDetailsView, setAssetDetailsView] = useState("pendingregisterAsset");
  const [editAsset, setEditAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQuery1, setSearchQuery1] = useState("");
  const [searchQuery2, setSearchQuery2] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const serverSearch = useDebouncedValue([searchQuery, searchQuery1, searchQuery2].filter(Boolean).join(" "));
  useEffect(() => setPage(1), [serverSearch, assetDetailsView]);
  const showActionButtons = !assetDetailsView.startsWith("myDepartment");

  const navigate = useNavigate();

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          "/api/categories/getCategory"
        );
        setCategories(response.data);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch user and assets when categories or view changes
  useEffect(() => {
    const fetchUserAndAssets = async () => {
      try {
        const currentUserLocal = JSON.parse(localStorage.getItem("currentUser"));
        if (!currentUserLocal?.username) return;

        const userResponse = await axios.get("/api/users/getallUsers");
        const userData = userResponse.data.find(u => u.username === currentUserLocal.username);
        if (!userData) return;
        setCurrentUser(userData);

        switch (assetDetailsView) {
          case "pendingregisterAsset":
            fetchAssets(userData);
            break;
          case "pendingtransferasset":
            fetchTransferAssets(userData);
            break;
          case "myDepartmentpendingregisterAsset":
            fetchMyDepartmentRegisterAssets(userData);
            break;
          case "myDepartmentpendingtransferasset":
            fetchMyDepartmentTransferAssets(userData);
            break;
          default:
            fetchAssets(userData);
        }
      } catch (err) {
        console.error("Error fetching user or assets:", err);
      }
    };

    if (categories.length > 0) fetchUserAndAssets();
  }, [categories, assetDetailsView, page, serverSearch]);

  // FETCH FUNCTIONS
  const fetchAssets = async (userData) => {
    try {
      const assetResponse = await axios.get(
        "/api/PendingAssetRegisterDetails/getPendingAssetDetails", { params: { paginated: true, page, limit: 100, search: serverSearch, company: userData.companyName } }
      );

      const filtered = assetResponse.data.assets.filter(asset => {
        if (asset.company !== userData.companyName) return false;

        if (
          asset.category === "Furniture, Fixtures & Office Equipment (FF&E)" &&
          asset.type === "Tables, Chairs, Cupboards"
        ) {
          return asset.department === userData.department;
        }

        const categoryData = categories.find(
          cat => cat.category === asset.category && cat.types === asset.type
        );
        if (!categoryData) return false;

        return categoryData.departments.includes(userData.department);
      });

      setAssetRegisterDetails(filtered);
      setPagination(assetResponse.data.pagination);
    } catch (err) {
      console.error("Error fetching assets:", err);
    }
  };

  const fetchTransferAssets = async (userData) => {
    try {
      const assetResponse = await axios.get(
        "/api/transfer/getPendingTransferAssetDetails", { params: { paginated: true, page, limit: 100, search: serverSearch, company: userData.companyName } }
      );

      const filtered = assetResponse.data.assets.filter(asset => {
        if (asset.company !== userData.companyName) return false;

        if (
          asset.category === "Furniture, Fixtures & Office Equipment (FF&E)" &&
          asset.type === "Tables, Chairs, Cupboards"
        ) {
          return asset.department === userData.department;
        }

        const categoryData = categories.find(
          cat => cat.category === asset.category && cat.types === asset.type
        );
        if (!categoryData) return false;

        return categoryData.departments.includes(userData.department);
      });

      setTransferAssetDetails(filtered);
      setPagination(assetResponse.data.pagination);
    } catch (err) {
      console.error("Error fetching transfer assets:", err);
    }
  };

  const fetchMyDepartmentRegisterAssets = async (userData) => {
    try {
      const assetResponse = await axios.get(
        "/api/PendingAssetRegisterDetails/getPendingAssetDetails", { params: { paginated: true, page, limit: 100, search: serverSearch, company: userData.companyName, department: userData.department } }
      );

      const filtered = assetResponse.data.assets.filter(
        asset =>
          asset.company === userData.companyName &&
          asset.department === userData.department
      );

      setAssetRegisterDetails(filtered);
      setPagination(assetResponse.data.pagination);
    } catch (err) {
      console.error("Error fetching my department register assets:", err);
    }
  };

  const fetchMyDepartmentTransferAssets = async (userData) => {
    try {
      const assetResponse = await axios.get(
        "/api/transfer/getPendingTransferAssetDetails", { params: { paginated: true, page, limit: 100, search: serverSearch, company: userData.companyName, department: userData.department } }
      );

      const filtered = assetResponse.data.assets.filter(
        asset =>
          asset.company === userData.companyName &&
          asset.department === userData.department
      );

      setTransferAssetDetails(filtered);
      setPagination(assetResponse.data.pagination);
    } catch (err) {
      console.error("Error fetching my department transfer assets:", err);
    }
  };

  useAutoRefresh(() => {
    if (!currentUser || categories.length === 0) return;

    switch (assetDetailsView) {
      case "pendingtransferasset":
        fetchTransferAssets(currentUser);
        break;
      case "myDepartmentpendingregisterAsset":
        fetchMyDepartmentRegisterAssets(currentUser);
        break;
      case "myDepartmentpendingtransferasset":
        fetchMyDepartmentTransferAssets(currentUser);
        break;
      default:
        fetchAssets(currentUser);
    }
  });

  // SEARCH FILTER
  const queryMatch = (query, asset) =>
    (asset.name?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.company?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.department?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.category?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.assetUpdateDate?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.assetTransferDate?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.type?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.assetName?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.assetUserName?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.assetModel?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.serialNumber?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.trackingId?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.subComponent?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.conditionType?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.invoiceDetails?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.specialNote?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.warranty?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.supplierName?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.invoiceNumber?.toLowerCase() || "").includes(query.toLowerCase()) ||
    (asset.purchaseDate?.toLowerCase() || "").includes(query.toLowerCase());

  const filteredAssets =
    assetDetailsView.includes("register")
      ? assetRegisterDetails.filter(asset =>
          [searchQuery, searchQuery1, searchQuery2].every(
            q => !q || queryMatch(q, asset)
          )
        )
      : transferAssetDetails.filter(asset =>
          [searchQuery, searchQuery1, searchQuery2].every(
            q => !q || queryMatch(q, asset)
          )
        );

  // HANDLE VIEW CHANGE
  const handleDetailsView = (e) => setAssetDetailsView(e.target.value);

  // EDIT ASSET HANDLING
  const handleEditClick = (asset) => setEditAsset(asset);

  

  

  const handleHandoverNote = (asset) => navigate("/HandoverNote", { state: { asset } });

  // EXPORT EXCEL
  const exportToExcel = () => {
    if (!filteredAssets.length) {
      alert("No filtered data available to export!");
      return;
    }

    const XLSX = require("xlsx");
    const cleanedData = filteredAssets.map(({ _id, computerComponents, createdAt, updatedAt, __v, ...rest }) => rest);
    const ws = XLSX.utils.json_to_sheet(cleanedData);
    ws['!cols'] = Object.keys(cleanedData[0]).map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Filtered Assets");
    XLSX.writeFile(wb, "Filtered_Asset_Details.xlsx");
  };

  return (
    <div className="row details2">
      <div className="col-md-14">
        <h1 className="assethead">Pending Asset Details</h1>
        <p>
          <Link to="/AuditDashBoard">Dashboard</Link> /
          <Link to="/PendingAllAsset"> Pending Asset Details</Link>
        </p>

        <button className="btnExcel" onClick={exportToExcel}>
          Export Filtered Assets to Excel
        </button>

        {/* Radio VIEW SWITCH */}
    <div className="radio-grid">
  {[
    { value: "pendingregisterAsset", label: "Pending Register Asset" },
    { value: "pendingtransferasset", label: "Pending Transfer Asset" },
    { value: "myDepartmentpendingregisterAsset", label: "My Department Pending Register Asset" },
    { value: "myDepartmentpendingtransferasset", label: "My Department Pending Transfer Asset" },
  ].map(opt => (
    <label key={opt.value} className="radio-card">
      <input
        type="radio"
        value={opt.value}
        name="view"
        checked={assetDetailsView === opt.value}
        onChange={handleDetailsView}
      />
      <div className="custom-circle"></div>
      <span>{opt.label}</span>
    </label>
  ))}
</div>

        {/* SEARCH BOXES */}
        <div className="row mb-2">
          <div className="col-md-4 col-12">
            <input
              className="form-control"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="col-md-4 col-12">
            <input
              className="form-control"
              placeholder="Search by category/type/name..."
              value={searchQuery1}
              onChange={(e) => setSearchQuery1(e.target.value)}
            />
          </div>
          <div className="col-md-4 col-12">
            <input
              className="form-control"
              placeholder="Search by model/serial/tracking..."
              value={searchQuery2}
              onChange={(e) => setSearchQuery2(e.target.value)}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="table-responsive mt-3">
          <table className="table table-bordered table-light custom_table">
            <thead className="thead-dark">
              <tr>
                <th>Register Name</th>
                <th>Company</th>
                <th>Department</th>
                <th>Category</th>
                <th>Condition Type</th>
                <th>Type</th>
                <th>Asset</th>
                <th>EPF No & Recipient Name</th>
                <th>Model</th>
                <th>Register Date</th>
                {assetDetailsView.includes("transfer") && <th>Transfer Date</th>}
                <th>Serial</th>
                <th>Tracking ID</th>
                <th>Price (Rs)</th>
                <th>Note</th>
                <th>Components</th>
                <th>Asset Image</th>
                <th>Invoice Image</th>
                <th>Warranty</th>
                <th>Supplier Name</th>
                <th>Invoice Number</th>
                <th>Purchase Date</th>
                <th>Handover Note</th>
                <th>Status</th>
               {/* {assetDetailsView.includes("register") || assetDetailsView.includes("transfer") ? <th>Edit</th> : null}*/}
               {showActionButtons && <th>Edit</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAssets.length > 0 ? filteredAssets.map(asset => (
                <tr key={asset._id}>
                  <td>{asset.name}</td>
                  <td>{asset.company}</td>
                  <td>{asset.department}</td>
                  <td>{asset.category}</td>
                  <td>{asset.conditionType}</td>
                  <td>{asset.type}</td>
                  <td>{asset.assetName}</td>
                  <td>{asset.assetUserName}</td>
                  <td>{asset.assetModel}</td>
                  <td>{asset.assetUpdateDate}</td>
                  {assetDetailsView.includes("transfer") && <td>{asset.assetTransferDate || "N/A"}</td>}
                  <td>{asset.serialNumber}</td>
                  <td>{asset.trackingId}</td>
                  <td>{asset.assetprice}</td>
                  <td>{asset.specialNote}</td>
                  <td>{asset.subComponent}</td>
                  <td>{asset.assetImageURL ? <a href={asset.assetImageURL} target="_blank" rel="noopener noreferrer">View Image</a> : "No Image"}</td>
                  <td>{asset.invoiceFileURL ? <a href={asset.invoiceFileURL} target="_blank" rel="noopener noreferrer">View Invoice</a> : "No Invoice"}</td>
                  <td>{asset.warranty}</td>
                  <td>{asset.supplierName}</td>
                  <td>{asset.invoiceDetails}</td>
                  <td>{asset.purchaseDate}</td>
                  {/*<td><button className="handoverbtn" onClick={() => handleHandoverNote(asset)}>Handover</button></td>*/}
                  <td>
  {showActionButtons ? (
    <button className="handoverbtn" onClick={() => handleHandoverNote(asset)}> <i className="fas fa-download"></i>
      Handover
    </button>
  ) : (
    "—"
  )}
</td>
                  <td><button className="status">Pending</button></td>
                 {/* {(assetDetailsView.includes("register") || assetDetailsView.includes("transfer")) && (
                    <td><button className="editstsatus" onClick={() => handleEditClick(asset)}>Edit</button></td>
                  )}*/}
                  {showActionButtons && (
  <td>
    <button className="editstsatus" onClick={() => handleEditClick(asset)}>
      Edit
    </button>
    <PendingAssetDeleteButton
      asset={asset}
      isTransfer={assetDetailsView.includes("transfer")}
      onDeleted={(id) =>
        assetDetailsView.includes("register")
          ? setAssetRegisterDetails((items) => items.filter((item) => item._id !== id))
          : setTransferAssetDetails((items) => items.filter((item) => item._id !== id))
      }
    />
  </td>
)}
                </tr>
              )) : (
                <tr>
                  <td colSpan="25" className="text-center">No matching asset data available</td>
                </tr>
              )}
            </tbody>
          </table>
          <AssetPagination page={pagination.page || page} totalPages={pagination.totalPages} total={pagination.total} onPageChange={setPage} />
        </div>

        {/* EDIT MODAL */}
        {editAsset && <PendingAssetEditor key={editAsset._id} asset={editAsset} onClose={() => setEditAsset(null)} onSaved={updated => { setEditAsset(null); setAssetRegisterDetails(prev => prev.map(item => item._id === updated._id ? updated : item)); }} />}
      </div>
    </div>
  );
};

export default PendingAllAsset;
